import { HelpCircle, LogOut, Settings } from "lucide-react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { menuData } from "@/config/settings/menu.tsx";
import NavButton from "@/components/application/LeftBar/NavButton.tsx";
import useAuth from "@integration/user/hooks/useAuth.ts";
import IconProG from "@/assets/svgComponets/IconProG";

export default function LeftBar({ isExpanded }: { isExpanded: boolean }) {
  const { isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <motion.div
      animate={{ width: isExpanded ? 220 : 80 }}
      className="h-screen pt-20 flex flex-col bg-gradient-to-b from-gray-900/95 to-gray-950/95 border-r border-gray-800/50 shadow-xl transition-all duration-300 relative overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-50 pointer-events-none"></div>
      <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-red-500/5 rounded-full filter blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

      {/* Logo or brand element at the top */}
      <div className="py-6 px-4 relative z-10 -ml-2">
        <motion.div
          className="flex items-center justify-center h-10"
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div
            className={`bg-gradient-to-br  flex items-center justify-center ${location.pathname === "/dashboard" ? "from-red-500/20 to-red-700/20 p-2 rounded-xl shadow-md border border-red-500/10" : "text-white"}`}
          >
            <Link to={"/dashboard"}>
              <IconProG />
            </Link>
          </div>
          {isExpanded && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="ml-3 font-bold text-lg bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent"
            >
              Dashboard {}
            </motion.span>
          )}
        </motion.div>
      </div>

      {/* Menu divider */}
      <div className="px-4 relative z-10">
        <div className="h-0.5 w-full bg-gradient-to-r from-gray-700/50 to-transparent mb-4"></div>
      </div>

      {/* Main menu items */}
      <div className="flex-1 flex flex-col items-start gap-2 px-4 py-2 relative z-10">
        {menuData.menu.map((item, index) => (
          <Link to={item.url} key={index} className="w-full">
            <NavButton
              icon={item.icon}
              label={item.label}
              isExpanded={isExpanded}
              isActive={location.pathname === item.url}
            />
          </Link>
        ))}
      </div>

      {/* Bottom menu divider */}
      <div className="px-4 relative z-10">
        <div className="h-0.5 w-full bg-gradient-to-r from-gray-700/50 to-transparent mb-4"></div>
      </div>

      {/* Bottom menu items */}
      {isAuthenticated && (
        <motion.div className="p-4 space-y-2 relative z-10">
          <NavButton
            icon={<Settings size={20} />}
            label="Configurações"
            isExpanded={isExpanded}
            isActive={location.pathname === "/settings"}
            onClick={() => {
              navigate("/settings");
            }}
          />
          <div className="hidden">
            <NavButton
              icon={<HelpCircle size={20} />}
              label="Suporte"
              isExpanded={isExpanded}
              isActive={location.pathname === "/support"}
              onClick={() => {
                navigate("/support");
              }}
            />
          </div>

          <NavButton
            icon={<LogOut size={20} />}
            label="Sair"
            isExpanded={isExpanded}
            onClick={logout}
          />
        </motion.div>
      )}
    </motion.div>
  );
}
